import { createAsyncThunk } from '@reduxjs/toolkit';
import { UrlRankRepositoryImpl } from '../Data/repositories/UrlRankRepositoryImpl';
import { UrlRankUseCase } from '../Domain/UseCase/UrlRankUseCase';
import {
  UrlRankError,
  UrlRankJobStatus,
  UrlRankRequest,
  UrlRankResult,
  isTerminalUrlRankStatus,
} from '../Model/UrlRank';
import { setLoading, setJob, setResult, setError, setPolling } from './reducers';

const repo = new UrlRankRepositoryImpl();
const useCase = new UrlRankUseCase(repo);

export type StartUrlRankThunkResult =
  | { kind: 'stored'; data: UrlRankResult }
  | { kind: 'job'; jobId: string };

export const startUrlRankCheck = createAsyncThunk<
  StartUrlRankThunkResult,
  UrlRankRequest,
  { rejectValue: UrlRankError }
>(
  'urlRank/start',
  async (request: UrlRankRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));

      const result = await useCase.start(request);

      if (result.isErr()) {
        dispatch(setError(result.error));
        return rejectWithValue(result.error);
      }

      const start = result.value;

      if (start.kind === 'stored') {
        dispatch(setResult(start.data));
        return { kind: 'stored', data: start.data };
      }

      dispatch(setJob({
        jobId: start.data.job_id,
        searchRange: start.data.search_range,
      }));
      return { kind: 'job', jobId: start.data.job_id };
    } catch (error) {
      const fallbackError: UrlRankError = {
        type: 'API_ERROR',
        message: '순위를 확인하지 못했습니다.',
      };
      dispatch(setError(fallbackError));
      return rejectWithValue(fallbackError);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const pollUrlRankJob = createAsyncThunk<
  UrlRankJobStatus,
  string,
  { rejectValue: UrlRankError }
>(
  'urlRank/poll',
  async (jobId: string, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPolling(true));
      const result = await useCase.getJob(jobId);

      if (result.isErr()) {
        dispatch(setError(result.error));
        return rejectWithValue(result.error);
      }

      const status = result.value;

      if (status.rank_check_status === 'failed') {
        const failedError: UrlRankError = {
          type: 'API_ERROR',
          message: '순위를 확인하지 못했습니다.',
        };
        dispatch(setError(failedError));
        return rejectWithValue(failedError);
      }

      if (isTerminalUrlRankStatus(status.rank_check_status)) {
        dispatch(setResult(status));
      }

      return status;
    } catch {
      const fallbackError: UrlRankError = {
        type: 'API_ERROR',
        message: '순위를 확인하지 못했습니다.',
      };
      dispatch(setError(fallbackError));
      return rejectWithValue(fallbackError);
    }
  }
);
